import React, { useState } from 'react';
import { ModalX } from '../../../components';
import { Button, Input, Option, Select, Textarea } from '@material-tailwind/react';
import { ArrowDownCircleIcon, ArrowUpCircleIcon, PackageXIcon } from 'lucide-react';
import { INVENTORY_TRANSACTION_TYPES } from '../../../utils/enums';
import { useAddInventoryTransaction, useGetInventoryConsumers, useAddInventoryConsumer } from '../../../custom-hooks/use-inventory';
import { formatQty, unitLabel } from '../utils';
import DateTimeField from './DateTimeField';
import LocationPicker from '../../maintenance/components/LocationPicker';

const TYPE_OPTIONS = [
    { value: INVENTORY_TRANSACTION_TYPES.OUT, label: 'Stock Out (used)', icon: ArrowDownCircleIcon, color: 'text-inv-brand' },
    { value: INVENTORY_TRANSACTION_TYPES.IN, label: 'Stock In (restock)', icon: ArrowUpCircleIcon, color: 'text-emerald-600' },
];

// Quick log-entry modal — the daily workhorse: pick a type, quantity, and who,
// in as few taps as possible (SRD target: under 10 seconds).
const AddTransactionModal = ({ isOpen, onClose, item }) => {
    const [type, setType] = useState(INVENTORY_TRANSACTION_TYPES.OUT);
    const [quantity, setQuantity] = useState('');
    const [consumer, setConsumer] = useState('');
    const [newConsumerName, setNewConsumerName] = useState('');
    const [note, setNote] = useState('');
    const [occurredAt, setOccurredAt] = useState(() => new Date());
    const [location, setLocation] = useState({ branch: '', building: '', floor: '' });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const { data: consumers = [] } = useGetInventoryConsumers();
    const addTransactionMutation = useAddInventoryTransaction();
    const addConsumerMutation = useAddInventoryConsumer();

    const isBreakage = type === INVENTORY_TRANSACTION_TYPES.BREAKAGE;
    const isDecrement = type !== INVENTORY_TRANSACTION_TYPES.IN;
    const qtyExceedsRemaining = isDecrement && Number(quantity) > Number(item?.currentRemaining ?? 0);
    // "Who" is required so every entry — including who broke/lost it — is attributable
    // to a person. The "Common use / unspecified" fallback below is kept for a possible
    // future reintroduction, but isn't offered as a choice right now.
    const consumerMissing = !consumer || (consumer === '__new__' && !newConsumerName.trim());
    // Breakage/loss always needs a reason on record.
    const noteMissing = isBreakage && !note.trim();
    // Stock leaving the system (out/breakage) happens at a real place; restocks (in)
    // go into central inventory and aren't tied to a floor.
    const locationMissing = isDecrement && (!location.branch || !location.building || !location.floor);

    const reset = () => {
        setType(INVENTORY_TRANSACTION_TYPES.OUT);
        setQuantity('');
        setConsumer('');
        setNewConsumerName('');
        setNote('');
        setOccurredAt(new Date());
        setLocation({ branch: '', building: '', floor: '' });
    };

    const handleClose = () => {
        reset();
        onClose();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        // Guard against double/triple clicks firing duplicate stock entries — the mutation's
        // own isLoading flag isn't set until after this handler starts, so a synchronous
        // local flag is needed to block re-entrant submits.
        if (isSubmitting) return;
        if (!item?._id || !quantity || qtyExceedsRemaining || consumerMissing || noteMissing || locationMissing) return;

        setIsSubmitting(true);
        try {
            let consumerId = consumer || null;
            if (consumer === '__new__' && newConsumerName.trim()) {
                const res = await addConsumerMutation.mutateAsync({ name: newConsumerName.trim() });
                consumerId = res?.data?.consumer?._id || null;
            }

            await addTransactionMutation.mutateAsync({
                itemId: item._id,
                data: {
                    type, quantity: Number(quantity), consumer: consumerId, note, occurredAt: occurredAt.toISOString(),
                    ...(isDecrement ? { branch: location.branch, building: location.building, floor: location.floor } : {}),
                },
            });
            handleClose();
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!item) return null;

    const typeChoices = item.tracksBreakage
        ? [...TYPE_OPTIONS, { value: INVENTORY_TRANSACTION_TYPES.BREAKAGE, label: 'Breakage / Loss', icon: PackageXIcon, color: 'text-red-600' }]
        : TYPE_OPTIONS;

    return (
        <ModalX isOpen={isOpen} onClose={handleClose} title={`Log Entry — ${item.name}`} maxWidth="md">
            <form onSubmit={handleSubmit} className="space-y-4">
                <div className="rounded-xl bg-inv-cream-100 px-3.5 py-2.5 text-sm text-[#6B675F]">
                    Currently <span className="font-bold text-inv-ink">{formatQty(item.currentRemaining, item.unitOfMeasure)}</span> remaining
                </div>

                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                    {typeChoices.map((opt) => {
                        const Icon = opt.icon;
                        const active = type === opt.value;
                        return (
                            <button
                                type="button"
                                key={opt.value}
                                onClick={() => setType(opt.value)}
                                className={`flex flex-col items-center gap-1.5 rounded-xl border px-3 py-3 text-xs font-semibold transition-all ${
                                    active ? 'border-inv-brand bg-inv-brand-light text-inv-brand' : 'border-inv-cream-200 text-[#6B675F] hover:bg-inv-cream-100'
                                }`}
                            >
                                <Icon size={20} className={active ? 'text-inv-brand' : opt.color} />
                                {opt.label}
                            </button>
                        );
                    })}
                </div>

                {isDecrement && (
                    <LocationPicker
                        branch={location.branch}
                        building={location.building}
                        floor={location.floor}
                        onChange={(updates) => setLocation((prev) => ({ ...prev, ...updates }))}
                        autoSelectSingleBranch
                        showSpace={false}
                    />
                )}

                <div>
                    <Input
                        type="number"
                        label={unitLabel('Quantity', item.unitOfMeasure)}
                        value={quantity}
                        onChange={(e) => setQuantity(e.target.value)}
                        min="0.01"
                        step="any"
                        required
                        autoFocus
                        error={qtyExceedsRemaining}
                    />
                    {qtyExceedsRemaining && (
                        <p className="mt-1 text-xs font-medium text-red-600">
                            Only {formatQty(item.currentRemaining, item.unitOfMeasure)} remaining — reduce the quantity.
                        </p>
                    )}
                </div>

                <DateTimeField label="Date & Time" value={occurredAt} onChange={setOccurredAt} max={new Date()} />

                {/* `selected` computes the closed-box label ourselves from state we control —
                    Material Tailwind's own display lookup is unreliable for options backed
                    by data loaded after mount (confirmed: value/onChange/submit all work
                    correctly, only its internal label rendering doesn't keep up). */}
                <Select
                    label={<>{isBreakage ? 'Broken/lost by' : 'Who'} <span className="text-red-500">*</span></>}
                    value={consumer}
                    onChange={(v) => setConsumer(v)}
                    menuProps={{ className: 'max-h-72' }}
                    selected={() => {
                        if (consumer === '__new__') return '+ Add new person…';
                        return consumers.find((c) => c._id === consumer)?.name || '';
                    }}
                >
                    {/* Common use / unspecified — disabled for now so every entry is
                        attributable to a person; may be reintroduced later. */}
                    {/* <Option value="">Common use / unspecified</Option> */}
                    {consumers.map((c) => (
                        <Option key={c._id} value={c._id}>{c.name}</Option>
                    ))}
                    <Option value="__new__">+ Add new person…</Option>
                </Select>

                {consumer === '__new__' && (
                    <Input
                        label="Type a name"
                        value={newConsumerName}
                        onChange={(e) => setNewConsumerName(e.target.value)}
                        required
                    />
                )}

                <Textarea
                    label={isBreakage ? 'Reason for breakage/loss' : 'Note (optional)'}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    rows={2}
                    required={isBreakage}
                />

                <div className="flex justify-end gap-2 pt-1">
                    <Button variant="text" className="rounded-full" onClick={handleClose} type="button">Cancel</Button>
                    <Button
                        type="submit"
                        className="rounded-full bg-inv-brand shadow-none hover:bg-inv-brand-dark"
                        loading={isSubmitting}
                        disabled={isSubmitting || !quantity || qtyExceedsRemaining || consumerMissing || noteMissing || locationMissing}
                    >
                        Log Entry
                    </Button>
                </div>
            </form>
        </ModalX>
    );
};

export default AddTransactionModal;
